import NextLink from "next/link";
import { Box, Heading, Text } from "@cruk/cruk-react-components";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/HomePageSearchResult/styles.module.css";

import {
  type SearchResultType,
  type SearchResultPageType,
  type SearchResultTeamType,
  type SearchTeamMemberType,
  type SearchOwnerType,
} from "@fwa/src/types";

const memberPageCrukSvg = "/assets/images/img/member-page-cruk-128.svg";
const memberTeamCrukSvg = "/assets/images/img/member-team-cruk-128.svg";
const inMemoryIconCruk = "/assets/images/img/in-memory-cruk-128.svg";

export const HomePageSearchResult = ({
  result: { _source: source, _index },
}: {
  result: SearchResultType<SearchResultTeamType | SearchResultPageType>;
}) => {
  const isResultATeamResult = _index.startsWith("fundraisingteams");
  const sourceAsTeam = source as SearchResultTeamType;
  const sourceAsPage = source as SearchResultPageType;

  const title = isResultATeamResult
    ? sourceAsTeam?.title || "Team"
    : sourceAsPage?.title || "Page";
  const description = isResultATeamResult
    ? sourceAsTeam?.description || ""
    : sourceAsPage?.description || "";

  const icon = isResultATeamResult
    ? memberTeamCrukSvg
    : sourceAsPage?.in_memory?.name
      ? inMemoryIconCruk
      : memberPageCrukSvg;

  const inMemoryString = isResultATeamResult
    ? null
    : sourceAsPage?.in_memory?.name
      ? `In memory of: ${sourceAsPage?.in_memory?.name}`
      : null;

  const membersString = isResultATeamResult
    ? (() => {
        const teamLeader: SearchTeamMemberType | null =
          sourceAsTeam?.team_leaders?.[0] || null;
        const teamMembersSource = sourceAsTeam?.team_members || [];
        const teamMembers: SearchTeamMemberType[] = [...teamMembersSource];
        const teamMemberNamesNoLeader: string[] = teamMembers
          .filter((member: SearchTeamMemberType) => member !== teamLeader)
          .map((member: SearchTeamMemberType) => member.name)
          .filter(
            (member: string, index: number, array: string[]) =>
              array.indexOf(member) === index, // deduplicate
          );
        const fullNamesArray = [
          ...(teamLeader ? [teamLeader.name] : []),
          ...teamMemberNamesNoLeader,
        ];
        return fullNamesArray.length > 4
          ? `${fullNamesArray.length} members including: ${fullNamesArray
              .filter((_item, index: number) => index < 5)
              .join(", ")}`
          : `Members: ${fullNamesArray.join(", ")}`;
      })()
    : sourceAsPage?.owners
        .map((member: SearchOwnerType) => member.name)
        .join(", ");

  const eventString = isResultATeamResult
    ? sourceAsTeam?.events?.length
      ? `Events: ${sourceAsTeam?.events.map((event) => event.title).join(", ")}`
      : null
    : sourceAsPage?.events?.length
      ? `Events: ${sourceAsPage?.events.map((event) => event.title).join(", ")}`
      : null;

  return (
    <NextLink
      href={source.path}
      aria-label={`${source.title}`}
      data-cta-type="search-item"
    >
      <span className={styles["item-link-span"]}>
        <div className={styles["search-result-card-wrapper-responsive"]}>
          <div className={styles["search-result-shadow-box"]}>
            <Box marginBottom="none" paddingRight="s">
              <div className={styles["icon-wrapper-circle"]}>
                <img src={icon} alt="" />
              </div>
            </Box>
            <div className={styles["text-content"]}>
              <Heading
                className={[
                  "component-heading",
                  "color-props",
                  "spacing-props",
                  sharedStyles["heading-multi-line-ellipsis"],
                ].join(" ")}
                h2
                textSize="m"
                textColor="primary"
                marginBottom="xs"
                style={{ WebkitLineClamp: 2 }}
              >
                {title}
              </Heading>

              {membersString && (
                <Text
                  className={[
                    sharedStyles["multi-line-ellipsis"],
                    "component-text",
                  ].join(" ")}
                  style={{ WebkitLineClamp: 3 }}
                >
                  {membersString}
                </Text>
              )}

              {inMemoryString && (
                <Text
                  className={[
                    sharedStyles["single-line-ellipsis"],
                    "component-text",
                  ].join(" ")}
                  style={{ WebkitLineClamp: 1 }}
                >
                  {inMemoryString}
                </Text>
              )}

              {eventString && (
                <Text
                  className={[
                    sharedStyles["single-line-ellipsis"],
                    "component-text",
                  ].join(" ")}
                  style={{ WebkitLineClamp: 1 }}
                >
                  {eventString}
                </Text>
              )}

              {description && (
                <Text
                  className={[
                    sharedStyles["single-line-ellipsis"],
                    "component-text",
                  ].join(" ")}
                  style={{ WebkitLineClamp: 1 }}
                >
                  {description}
                </Text>
              )}
            </div>
          </div>
        </div>
      </span>
    </NextLink>
  );
};

export default HomePageSearchResult;
